import {FormEvent, useEffect, useRef, useState} from 'react';
import {ProFenzMark} from './Wordmark';
import {SignupBotCheck} from './SignupBotCheck';
import {QuickServeDocumentIntake} from './QuickServeDocumentIntake';
import './QuickServeIntake.css';

/**
 * Guest "Quick Serve Link" intake — Issue #103. No account, no password.
 * A prospective customer follows a branded link, verifies their email by
 * clicking a real emailed link (never a typed code — Issue #156's corrected
 * pattern), then submits case details and documents in one step.
 *
 * This component calls the documented-but-not-yet-registered backend
 * contract in docs/ai/CLAUDE_QUICK_SERVE_INTAKE_UX_CONTRACT.md. Until Codex
 * wires those routes, every call below 404s and the component shows the
 * existing "temporarily unavailable" error state — the same pattern already
 * used by fenzsafeBackendAdapter.ts: build the real caller now, swap nothing
 * later.
 */

type ServiceLevel = 'standard' | 'rush';
type RequesterRole = 'law_firm' | 'business' | 'individual';
type Recipient = {subject: string; serviceAddress: string; deadlineDate: string};
type UploadedDocument = {documentId: string; name: string};
type UploadFailure = {id: number; name: string; message: string};

type Stage =
  | {kind: 'loading-link'}
  | {kind: 'invalid-link'}
  | {kind: 'email'; organizationName: string; siteKey: string}
  | {kind: 'check-email'; organizationName: string; email: string}
  | {kind: 'verifying'}
  | {kind: 'verify-failed'; reason: string}
  | {kind: 'intake'; organizationName: string; guestSession: string; email: string}
  | {kind: 'confirmed'; referenceNumber: string};

function linkTokenFromPath(pathname: string): string | null {
  const match = /^\/quick-serve\/([^/]+)\/?$/.exec(pathname);
  if (!match) return null;
  try { return decodeURIComponent(match[1]); } catch { return null; }
}

export function isQuickServePath(pathname: string): boolean {
  return /^\/quick-serve\/[^/]+\/?$/.test(pathname);
}

export type QuickServeLocation = {
  linkToken: string | null;
  hadVerifyParameter: boolean;
  verifyCredential: string | null;
};

export function captureQuickServeLocation(): QuickServeLocation {
  const url = new URL(window.location.href);
  const verifyValues = url.searchParams.getAll('verify');
  url.searchParams.delete('verify');
  if (verifyValues.length > 0) {
    const query = url.searchParams.toString();
    window.history.replaceState(window.history.state, '', `${url.pathname}${query ? `?${query}` : ''}${url.hash}`);
  }
  return {
    linkToken: linkTokenFromPath(url.pathname),
    hadVerifyParameter: verifyValues.length > 0,
    verifyCredential: verifyValues.length === 1 && verifyValues[0] !== '' ? verifyValues[0] : null,
  };
}

export function QuickServeIntake({location}: {location: QuickServeLocation}) {
  const {linkToken, hadVerifyParameter, verifyCredential} = location;
  const verificationRequest = useRef<Promise<{response: Response; body: Record<string, unknown>}> | null>(null);
  const [stage, setStage] = useState<Stage>({kind: 'loading-link'});
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    if (!linkToken) { setStage({kind: 'invalid-link'}); return; }
    if (hadVerifyParameter && !verifyCredential) {
      setStage({kind: 'verify-failed', reason: 'This link is no longer valid.'});
      return;
    }
    if (verifyCredential) {
      setStage({kind: 'verifying'});
      if (verificationRequest.current === null) {
        verificationRequest.current = (async () => {
          const r = await fetch(`/api/v1/quick-serve/${encodeURIComponent(linkToken)}/verify`, {
            method: 'POST', credentials: 'omit', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({credential: verifyCredential}),
          });
          return {response: r, body: await r.json() as Record<string, unknown>};
        })();
      }
      // React StrictMode re-runs effects in development. Reuse the same one-use request while
      // allowing the second effect subscription to receive its result after the first cleanup.
      void verificationRequest.current.then(({response: r, body}) => {
        if (!active) return;
        if (!r.ok || body.verified !== true || typeof body.guest_session !== 'string' || typeof body.organization_name !== 'string' || typeof body.email !== 'string') {
          setStage({kind: 'verify-failed', reason: r.status === 410 ? 'This link has expired.' : r.status === 409 ? 'This link was already used.' : 'This link is no longer valid.'});
          return;
        }
        setStage({kind: 'intake', organizationName: body.organization_name, guestSession: body.guest_session, email: body.email});
      }).catch(() => { if (active) setStage({kind: 'verify-failed', reason: 'We could not confirm this link. Check your connection and try again.'}); });
      return () => { active = false; };
    }
    void (async () => {
      try {
        const r = await fetch(`/api/v1/quick-serve/${encodeURIComponent(linkToken)}/config`, {credentials: 'omit', headers: {Accept: 'application/json'}});
        const body = await r.json();
        if (!active) return;
        if (!r.ok || body.enabled !== true || typeof body.organization_name !== 'string' || typeof body.site_key !== 'string') { setStage({kind: 'invalid-link'}); return; }
        setStage({kind: 'email', organizationName: body.organization_name, siteKey: body.site_key});
      } catch { if (active) setStage({kind: 'invalid-link'}); }
    })();
    return () => { active = false; };
  }, [hadVerifyParameter, linkToken, verifyCredential]);

  if (stage.kind === 'loading-link' || stage.kind === 'verifying') {
    return <main className="shell access-shell quick-serve-shell"><p role="status">{stage.kind === 'verifying' ? 'Confirming your email…' : 'Loading…'}</p></main>;
  }
  if (stage.kind === 'invalid-link') {
    return <main className="shell access-shell quick-serve-shell"><header className="hero access-hero"><ProFenzMark context="login-card"/></header>
      <section className="panel access-panel"><h1>This link isn't available</h1><p>It may be expired, revoked, or mistyped. Contact the company that sent it to you for a new link.</p></section></main>;
  }
  if (stage.kind === 'verify-failed') {
    return <main className="shell access-shell quick-serve-shell"><header className="hero access-hero"><ProFenzMark context="login-card"/></header>
      <section className="panel access-panel"><h1>We couldn't confirm your email</h1><p role="alert">{stage.reason}</p><p>Go back to the original link and enter your email again to get a new verification email.</p></section></main>;
  }
  if (stage.kind === 'email' || stage.kind === 'check-email') return <QuickServeEmailStep linkToken={linkToken!} stage={stage} onSent={(email) => setStage(current => current.kind === 'email' ? {kind: 'check-email', organizationName: current.organizationName, email} : current)}/>;
  if (stage.kind === 'confirmed') {
    return <main className="shell access-shell quick-serve-shell"><header className="hero access-hero"><ProFenzMark context="login-card"/></header>
      <section className="panel access-panel"><h1>Request received</h1>
        <p>Your reference number is <strong>{stage.referenceNumber}</strong>. Keep it for your records.</p>
        <p>The receiving company will review your request before service begins.</p>
      </section></main>;
  }
  return <QuickServeIntakeForm linkToken={linkToken!} organizationName={stage.organizationName} guestSession={stage.guestSession} email={stage.email}
    onSubmitted={(referenceNumber) => setStage({kind: 'confirmed', referenceNumber})} error={error} setError={setError}/>;
}

function QuickServeEmailStep({linkToken, stage, onSent}: {linkToken: string; stage: Extract<Stage,{kind:'email'|'check-email'}>; onSent: (email: string) => void}) {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(''), [reset, setReset] = useState(0), [pending, setPending] = useState(false), [error, setError] = useState('');
  const busy = useRef(false);

  if (stage.kind === 'check-email') {
    return <main className="shell access-shell quick-serve-shell"><header className="hero access-hero"><ProFenzMark context="login-card"/><h1>Check your email</h1></header>
      <section className="panel access-panel"><p>We sent a link to <strong>{stage.email}</strong>. Open it to continue — no code to type.</p><p>The link expires soon and can only be used once. If you don't see it, check spam or request another link from the original source.</p></section></main>;
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy.current || !token) return;
    busy.current = true; setPending(true); setError('');
    try {
      const r = await fetch(`/api/v1/quick-serve/${encodeURIComponent(linkToken)}/start`, {
        method: 'POST', credentials: 'omit', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({email: email.trim(), bot_token: token}),
      });
      const body = await r.json();
      if (!r.ok || body.accepted !== true) throw new Error();
      onSent(email.trim());
    } catch { setError('We could not send the verification email. Please wait a moment and try again.'); }
    finally { busy.current = false; setPending(false); setToken(''); setReset(n => n + 1); }
  }

  return <main className="shell access-shell quick-serve-shell"><header className="hero access-hero"><ProFenzMark context="login-card"/><p className="eyebrow">SEND DOCUMENTS FOR SERVICE</p><h1>{stage.organizationName}</h1><p>No account required. Enter your email to begin.</p></header>
    <section className="panel access-panel" aria-labelledby="quick-serve-email-title"><h2 id="quick-serve-email-title">Start your request</h2>
      {error && <p className="notice error" role="alert">{error}</p>}
      <form className="login-form" onSubmit={event => void submit(event)} noValidate>
        <label htmlFor="quick-serve-email">Your email</label>
        <input id="quick-serve-email" type="email" autoComplete="email" autoCapitalize="none" required maxLength={254} value={email} onChange={e => setEmail(e.target.value)}/>
        <SignupBotCheck siteKey={stage.siteKey} action="quick_serve_start" reset={reset} onToken={setToken}/>
        <button type="submit" disabled={!token || pending}>{pending ? 'Sending…' : 'Send verification email'}</button>
      </form>
    </section></main>;
}

function emptyRecipient(): Recipient { return {subject: '', serviceAddress: '', deadlineDate: ''}; }

function QuickServeIntakeForm({linkToken, organizationName, guestSession, email, onSubmitted, error, setError}: {
  linkToken: string; organizationName: string; guestSession: string; email: string;
  onSubmitted: (referenceNumber: string) => void; error: string; setError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<RequesterRole>('law_firm');
  const [plaintiff, setPlaintiff] = useState('');
  const [defendant, setDefendant] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [court, setCourt] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [county, setCounty] = useState('');
  const [serviceLevel, setServiceLevel] = useState<ServiceLevel>('standard');
  const [recipients, setRecipients] = useState<Recipient[]>([emptyRecipient()]);
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFailures, setUploadFailures] = useState<UploadFailure[]>([]);
  const nextUploadFailureId = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const uploadInFlight = useRef(false);
  const submitInFlight = useRef(false);

  function updateRecipient(index: number, patch: Partial<Recipient>) {
    setRecipients(current => current.map((recipient, i) => i === index ? {...recipient, ...patch} : recipient));
  }
  function addRecipient() { setRecipients(current => [...current, emptyRecipient()]); }
  function removeRecipient(index: number) { setRecipients(current => current.length > 1 ? current.filter((_, i) => i !== index) : current); }

  function recordUploadFailure(file: File, message: string) {
    const failure = {id: ++nextUploadFailureId.current, name: file.name, message};
    setUploadFailures(current => [...current, failure]);
  }

  async function uploadFiles(files: File[]) {
    if (files.length === 0 || uploadInFlight.current || submitInFlight.current) return;
    uploadInFlight.current = true;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size < 1 || file.size > 25 * 1024 * 1024) {
          recordUploadFailure(file, file.size < 1 ? 'This file is empty and was not uploaded.' : 'This file exceeds 25 MB and was not uploaded.');
          continue;
        }
        try {
          const r = await fetch(`/api/v1/quick-serve/${encodeURIComponent(linkToken)}/documents`, {
            method: 'POST', credentials: 'omit',
            headers: {'Content-Type': file.type || 'application/octet-stream', 'X-ProFenz-Filename': encodeURIComponent(file.name), 'X-ProFenz-Guest-Session': guestSession},
            body: file,
          });
          const body = await r.json();
          if (!r.ok || typeof body.document_id !== 'string' || !body.document_id.trim()) {
            recordUploadFailure(file, 'Upload could not be confirmed. Check with the receiving company before uploading it again.');
            continue;
          }
          setDocuments(current => current.some(d => d.documentId === body.document_id) ? current : [...current, {documentId: body.document_id, name: file.name}]);
        } catch {
          recordUploadFailure(file, 'Upload could not be confirmed. Check with the receiving company before uploading it again.');
        }
      }
    } finally { uploadInFlight.current = false; setUploading(false); }
  }
  function removeDocument(documentId: string) { setDocuments(current => current.filter(d => d.documentId !== documentId)); }

  function applyDocumentReview(values: {fields: {plaintiff?: string; defendant?: string; caseNumber?: string; stateCode?: string; county?: string; court?: string}; recipients: {subject: string; serviceAddress: string}[]}, file: File) {
    if (values.fields.plaintiff) setPlaintiff(values.fields.plaintiff);
    if (values.fields.defendant) setDefendant(values.fields.defendant);
    if (values.fields.caseNumber) setCaseNumber(values.fields.caseNumber);
    if (values.fields.stateCode) setStateCode(values.fields.stateCode);
    if (values.fields.county) setCounty(values.fields.county);
    if (values.fields.court) setCourt(values.fields.court);
    if (values.recipients.length > 0) {
      setRecipients(values.recipients.map(r => ({...emptyRecipient(), subject: r.subject, serviceAddress: r.serviceAddress})));
    }
    void uploadFiles([file]);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitInFlight.current || uploadInFlight.current) return;
    if (uploadFailures.length > 0) return;
    if (!name.trim() || !phone.trim()) { setError('Enter your name and phone number.'); return; }
    if (!plaintiff.trim() || !defendant.trim() || !caseNumber.trim() || !court.trim() || !stateCode.trim()) { setError('Enter the plaintiff, defendant, case number, state and court as they appear on the court papers.'); return; }
    if (recipients.some(r => !r.subject.trim() || !r.serviceAddress.trim() || !r.deadlineDate.trim())) { setError('Enter who is being served, their service address, and a service deadline for every row.'); return; }
    submitInFlight.current = true; setSubmitting(true); setError('');
    try {
      const r = await fetch(`/api/v1/quick-serve/${encodeURIComponent(linkToken)}/intake`, {
        method: 'POST', credentials: 'omit', headers: {'Content-Type': 'application/json', Accept: 'application/json', 'X-ProFenz-Guest-Session': guestSession},
        body: JSON.stringify({
          requester: {name: name.trim(), phone: phone.trim(), role},
          case: {plaintiff: plaintiff.trim(), defendant: defendant.trim(), case_number: caseNumber.trim(), court: court.trim(), state_code: stateCode.trim(), county: county.trim() || undefined},
          service_level: serviceLevel,
          recipients: recipients.map(r => ({subject: r.subject.trim(), service_address: r.serviceAddress.trim(), deadline_date: r.deadlineDate.trim()})),
          document_ids: documents.map(d => d.documentId),
          notes: notes.trim() || undefined,
        }),
      });
      const body = await r.json();
      if (!r.ok || body.accepted !== true || typeof body.reference_number !== 'string') throw new Error();
      onSubmitted(body.reference_number);
    } catch { setError('Your request could not be submitted. Your entries are preserved — please try again.'); }
    finally { submitInFlight.current = false; setSubmitting(false); }
  }

  return <main className="shell quick-serve-shell"><header className="hero"><ProFenzMark context="login-card"/><p className="eyebrow">{organizationName}</p><h1>Tell us who needs to be served</h1><p>Verified as <strong>{email}</strong>.</p></header>
    <section className="panel">
      <QuickServeDocumentIntake linkToken={linkToken} guestSession={guestSession} disabled={submitting || uploading} onReviewed={applyDocumentReview}/>
    </section>
    <section className="panel" aria-labelledby="quick-serve-intake-title"><h2 id="quick-serve-intake-title">Request details</h2>
      {error && <p className="notice error" role="alert">{error}</p>}
      <form className="intake-form quick-serve-form" onSubmit={event => void submit(event)} noValidate>
        <div className="field"><label htmlFor="qs-name">Your name</label><input id="qs-name" disabled={submitting} required maxLength={191} value={name} onChange={e => setName(e.target.value)}/></div>
        <div className="field"><label htmlFor="qs-phone">Phone</label><input id="qs-phone" disabled={submitting} type="tel" required maxLength={32} value={phone} onChange={e => setPhone(e.target.value)}/></div>
        <div className="field"><label htmlFor="qs-role">I'm requesting for</label>
          <select id="qs-role" disabled={submitting} value={role} onChange={e => setRole(e.target.value as RequesterRole)}>
            <option value="law_firm">Law firm or attorney</option><option value="business">Business or organization</option><option value="individual">Individual or self-represented party</option>
          </select></div>
        <div className="field"><label htmlFor="qs-service-level">Service level</label>
          <select id="qs-service-level" disabled={submitting} value={serviceLevel} onChange={e => setServiceLevel(e.target.value as ServiceLevel)}>
            <option value="standard">Standard</option><option value="rush">Rush</option>
          </select></div>

        <fieldset className="quick-serve-case" disabled={submitting}><legend>Case information</legend>
          <p className="form-note">Filled in automatically if we could read it from your court papers above — check it over, and correct or fill in anything we couldn't read.</p>
          <div className="field"><label htmlFor="qs-plaintiff">Plaintiff</label><textarea id="qs-plaintiff" rows={2} required maxLength={4000} value={plaintiff} onChange={e => setPlaintiff(e.target.value)} placeholder="Full name of plaintiff or plaintiffs"/></div>
          <div className="field"><label htmlFor="qs-defendant">Defendant</label><textarea id="qs-defendant" rows={2} required maxLength={4000} value={defendant} onChange={e => setDefendant(e.target.value)} placeholder="Full name of defendant or defendants"/></div>
          <div className="field"><label htmlFor="qs-case-number">Case number</label><input id="qs-case-number" required maxLength={96} value={caseNumber} onChange={e => setCaseNumber(e.target.value)} placeholder="Copy exactly from the court papers"/></div>
          <div className="field"><label htmlFor="qs-state">State</label><input id="qs-state" required maxLength={64} value={stateCode} onChange={e => setStateCode(e.target.value)} placeholder="State of the issuing court"/></div>
          <div className="field"><label htmlFor="qs-county">County or court district (optional)</label><input id="qs-county" maxLength={191} value={county} onChange={e => setCounty(e.target.value)} placeholder="As shown on the court papers"/></div>
          <div className="field"><label htmlFor="qs-court">Court</label><input id="qs-court" required maxLength={191} value={court} onChange={e => setCourt(e.target.value)} placeholder="Name of the issuing court"/></div>
        </fieldset>

        <fieldset className="quick-serve-recipients" disabled={submitting}><legend>Who needs to be served</legend>
          {recipients.map((recipient, index) => <div className="quick-serve-recipient-row" key={index}>
            <div className="field"><label htmlFor={`qs-subject-${index}`}>Person or business to serve</label>
              <input id={`qs-subject-${index}`} required maxLength={500} value={recipient.subject} onChange={e => updateRecipient(index, {subject: e.target.value})} placeholder="Jordan Rivera"/></div>
            <div className="field"><label htmlFor={`qs-address-${index}`}>Service address</label>
              <textarea id={`qs-address-${index}`} required rows={2} maxLength={1000} value={recipient.serviceAddress} onChange={e => updateRecipient(index, {serviceAddress: e.target.value})}/></div>
            <div className="field"><label htmlFor={`qs-deadline-${index}`}>Service deadline</label>
              <input id={`qs-deadline-${index}`} type="date" required value={recipient.deadlineDate} onChange={e => updateRecipient(index, {deadlineDate: e.target.value})}/></div>
            {recipients.length > 1 && <button type="button" className="button-link" onClick={() => removeRecipient(index)}>Remove this recipient</button>}
          </div>)}
          <button type="button" className="button-link" onClick={addRecipient}>Add another person to serve</button>
        </fieldset>

        <div className="field quick-serve-documents"><label htmlFor="qs-documents">More documents (optional)</label>
          <input id="qs-documents" type="file" multiple disabled={uploading || submitting} onChange={e => { void uploadFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }}/>
          <p className="form-note">Files are sent only when the intake service is available. This page does not use browser storage.</p>
          {uploading && <p role="status">Uploading…</p>}
          {uploadFailures.length > 0 && <div className="notice error" role="alert">
            <p>Some selected documents are not confirmed attached. Review each one before submitting. Continue without an upload only if you intend to leave it out of this request; this does not delete any uploaded copy.</p>
            <ul>{uploadFailures.map(failure => <li key={failure.id}>
              <strong>{failure.name}</strong> — {failure.message}
              <button type="button" className="button-link" disabled={uploading || submitting}
                aria-label={`Continue without ${failure.name} (upload ${failure.id})`}
                onClick={() => setUploadFailures(current => current.filter(item => item.id !== failure.id))}>Continue without this upload</button>
            </li>)}</ul>
          </div>}
          {documents.length > 0 && <ul className="quick-serve-document-list">{documents.map(doc => <li key={doc.documentId}>{doc.name} <button type="button" className="button-link" disabled={submitting} onClick={() => removeDocument(doc.documentId)}>Remove</button></li>)}</ul>}
        </div>

        <div className="field"><label htmlFor="qs-notes">Anything else we should know (optional)</label><textarea id="qs-notes" disabled={submitting} rows={3} maxLength={2000} value={notes} onChange={e => setNotes(e.target.value)}/></div>

        <button type="submit" disabled={submitting || uploading || uploadFailures.length > 0}>{submitting ? 'Submitting…' : 'Submit request'}</button>
      </form>
    </section></main>;
}
